import React, { useEffect } from "react";
import {
  BrowserRouter,
  Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useExternalHistory } from "hooks/useExternalHistory";
import loadable from "@loadable/component";
import environment from "config";
import { initAnalytics } from "libs/amplitudeHelper";
import useHotjar from "libs/useHotjar";

import { useAuth, useRefreshToken } from "components/app/auth/useAuth";
import Loading from "components/app/Loading";
import "react-bnb-gallery/dist/style.css";
import { useAgenciaContext } from "context/agencia/agencia.context";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isUserTypeInMaintenance } from "./underMaintenanceConfig";

const SignIn = loadable(() => import("components/app/auth/SignIn"));
const SignOut = loadable(() => import("components/app/auth/SignOut"));
const Callback = loadable(() => import("components/app/auth/Callback"));
const Home = loadable(() => import("components/app/Home"));
const Publicacion = loadable(
  () => import("components/app/publicacion/nueva/Publicacion"),
);
const Cotizador = loadable(() => import("components/app/cotizador/Cotizador"));
const EscanearQR = loadable(() => import("./escanearQRStepper/EscanearQR"));
const Perfil = loadable(() => import("./perfil/Perfil"));
const HomeInterno = loadable(() => import("./HomeInterno"));
const TerminosYCondiciones = loadable(
  () => import("./TyC/TerminosYCondiciones"),
);
const Maintenance = loadable(() => import("./maintenance/Maintenance"));

interface IAppProps {
  history?: any;
}

const App = ({ history }: IAppProps) => {
  const { isAuthenticated, isLoading: loadingUser, user } = useAuth();
  const flags = useFlags();

  const historyLocation = useExternalHistory(history);

  useRefreshToken();
  useHotjar();

  useEffect(() => {
    initAnalytics("v1.0", environment.amplitudeKey);
  }, []);

  // Single maintenance check per user type
  if (isUserTypeInMaintenance(flags, user?.userType)) {
    return <Maintenance />;
  }

  const routes = (
    <>
      {loadingUser && <Loading />}
      {!loadingUser && !isAuthenticated && <PublicRoutes />}
      {!loadingUser && isAuthenticated && user.userType === "Agenciero" && (
        <AgencieroRoutes />
      )}
      {!loadingUser && isAuthenticated && user.userType === "Interno" && (
        <InternoRoutes />
      )}
      {!loadingUser && isAuthenticated && user.userType === "Productor" && (
        <ProductorRoutes />
      )}
    </>
  );

  // If history is provided (microfrontend mode), use Router with external history
  // Otherwise use BrowserRouter (standalone mode)
  return history && historyLocation ? (
    <Router location={historyLocation} navigator={history}>
      {routes}
    </Router>
  ) : history ? (
    // History provided but location not yet initialized - show loading
    <Loading />
  ) : (
    <BrowserRouter>{routes}</BrowserRouter>
  );
};

const PublicRoutes = () => (
  <Routes>
    <Route path="/signin" element={<SignIn />} />
    <Route path="/callback" element={<Callback />} />
    <Route path="*" element={<Navigate to="/signin" replace />} />
  </Routes>
);

const InternoRoutes = () => (
  <Routes>
    <Route path="/autos" element={<HomeInterno />} />
    <Route path="/autos/cotizador" element={<Cotizador />} />
    <Route path="/autos/escanearqr" element={<EscanearQR />} />
    <Route path="/signout" element={<SignOut />} />
    <Route path="*" element={<Navigate to="/autos" replace />} />
  </Routes>
);

const ProductorRoutes = () => {
  const { cotizarVehiculos } = useFlags();

  return (
    <Routes>
      {cotizarVehiculos && (
        <Route path="/autos/cotizador" element={<Cotizador />} />
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const AgencieroRoutes = () => {
  const { agencia, loading } = useAgenciaContext();
  const { cotizarVehiculos } = useFlags();
  return (
    <Routes>
      {!loading && !agencia && AgenciaNoCreadaRoutes(cotizarVehiculos)}
      {!loading && agencia && AgenciaCreadaRoutes(cotizarVehiculos)}
    </Routes>
  );
};

const AgenciaCreadaRoutes = (cotizarVehiculos: boolean) => {
  return [
    <Route key="route-autos" path="/autos" element={<Home />} />,
    <Route
      key="route-publicacion"
      path="/autos/publicacion"
      element={<Publicacion />}
    />,
    cotizarVehiculos ? (
      <Route
        key="route-cotizador"
        path="/autos/cotizador"
        element={<Cotizador />}
      />
    ) : null,
    <Route
      key="route-publicacion-id"
      path="/autos/publicacion/:id"
      element={<Publicacion />}
    />,
    <Route key="route-qr" path="/autos/escanearqr" element={<EscanearQR />} />,
    <Route key="route-perfil" path="/autos/perfil" element={<Perfil />} />,
    <Route key="route-signout" path="/signout" element={<SignOut />} />,
    <Route
      key="redirect-autos"
      path="*"
      element={<Navigate to="/autos" replace />}
    />,
  ];
};
const AgenciaNoCreadaRoutes = (cotizarVehiculos: boolean) => {
  const navigate = useNavigate();

  useEffect(() => {
    const vioTerminosYCondiciones = localStorage.getItem(
      "vioTerminosYCondicionesPrendo",
    )
      ? true
      : false;
    if (!vioTerminosYCondiciones) navigate("/autos/terminosycondiciones");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [
    <Route key="route-autos" path="/autos" element={<Home />} />,
    cotizarVehiculos ? (
      <Route
        key="route-cotizador"
        path="/autos/cotizador"
        element={<Cotizador />}
      />
    ) : null,
    <Route
      key="route-tyc"
      path="/autos/terminosycondiciones"
      element={<TerminosYCondiciones />}
    />,
    <Route key="route-signout" path="/signout" element={<SignOut />} />,
    <Route
      key="redirect-tyc"
      path="*"
      element={<Navigate to="/autos/terminosycondiciones" replace />}
    />,
  ];
};

export default App;
